import { Injectable, NgZone } from '@angular/core';

import * as _ from 'lodash';

import { Draggable } from './classes/draggable';
import { DropZone } from './classes/drop-zone';
import { Subscription } from 'rxjs';

interface DropZoneRegisterParams {
  element: HTMLElement;
}

interface DraggableRegisterParams {
  element: HTMLElement;
}

interface DragHandlerSubscriptions {
  [id: number]: {
    [key: string]: Subscription;
  };
}

export type DragAction = 'move' | 'copy';

export interface DragAndDropEvent {
  action: DragAction;
  data: any;
  event: DragEvent;
}

@Injectable()
export class DragAndDropService {

  constructor(
    private _ngZone: NgZone
  ) {
    this.addEventListener(document, 'dragenter', this._checkLeave);
  }

  public active_draggable: Draggable = null;
  public active_drop_zone: DropZone = null;

  private _draggable_id = 0;
  private _draggables: Draggable[] = [];
  private _draggable_subscriptions: DragHandlerSubscriptions = {};

  private _drop_zone_id = 0;
  private _drop_zones: DropZone[] = [];
  private _drop_zone_subscriptions: DragHandlerSubscriptions = {};

  public registerDraggable(params: DraggableRegisterParams): Draggable {
    const _draggable = new Draggable(
      this,
      this._draggable_id,
      params.element
    );

    this._draggable_subscriptions[_draggable.id] = {
      onStart: _draggable.$onStart.subscribe((event: DragEvent) => {
        this.active_draggable = _draggable;

        _draggable.setDragging(true);
        _draggable.onStart.emit(this.getEvent(event));
      }),
      onEnd: _draggable.$onEnd.subscribe((event: DragEvent) => {
        this._drop_zones.forEach((drop_zone) => {
          if (drop_zone.dragging_over) {
            drop_zone.$onLeave.emit();
          }
        });

        _draggable.setDragging(false);
        _draggable.onEnd.emit(this.getEvent(event));

        this.active_draggable = null;
      })
    };

    this._draggables.push(_draggable);

    this._draggable_id++;

    return _draggable;
  }

  public unregisterDraggable(draggable: Draggable): void {
    const index = this._draggables.findIndex((_draggable) => _draggable.id == draggable.id);
    if (index >= 0) { this._draggables.splice(index, 1); }

    const subscriptions = this._draggable_subscriptions[draggable.id];
    Object.keys(subscriptions).forEach((key) => subscriptions[key].unsubscribe());
    delete this._draggable_subscriptions[draggable.id];

    draggable.destroy();
  }

  public registerDropZone(params: DropZoneRegisterParams): DropZone {
    const _drop_zone = new DropZone(
      this,
      this._drop_zone_id,
      params.element
    );

    this._drop_zone_subscriptions[_drop_zone.id] = {
      onEnter: _drop_zone.$onEnter.subscribe((event: DragEvent) => {
        if (this.isDropZoneRestricted(_drop_zone)) { return; }
        if (_drop_zone.dragging_over) { return; }

        if (this.active_drop_zone && this.active_drop_zone.id !== _drop_zone.id) {
          this.active_drop_zone.$onLeave.emit();
        }

        this.active_drop_zone = _drop_zone;

        _drop_zone.setDraggingOver(true);
        _drop_zone.onEnter.emit(this.getEvent(event));
      }),
      onLeave: _drop_zone.$onLeave.subscribe(() => {
        if (this.active_drop_zone && this.active_drop_zone.id === _drop_zone.id) {
          this.active_drop_zone = null;
        }

        _drop_zone.setDraggingOver(false);
        _drop_zone.onLeave.emit(this.getEvent());
      }),
      onDrop: _drop_zone.$onDrop.subscribe((event: DragEvent) => {
        if (this.isDropZoneRestricted(_drop_zone)) { return; }

        if (this.active_draggable && this.active_draggable.action === 'move') {
          // Check and emit "onRemove" event
          this._drop_zones.forEach((drop_zone) => {
            if (_drop_zone.id !== drop_zone.id) {
              const draggable_index = drop_zone.children.findIndex((draggable) => draggable.id === this.active_draggable.id);
              if (draggable_index >= 0) { drop_zone.onRemove.emit(this.getEvent(event)); }
            }
          });
        }

        _drop_zone.onDrop.emit(this.getEvent(event));
        _drop_zone.$onLeave.emit();
      })
    };

    this._drop_zones.push(_drop_zone);

    this._drop_zone_id++;

    return _drop_zone;
  }

  public unregisterDropZone(drop_zone: DropZone): void {
    const index = this._drop_zones.findIndex((_drop_zone) => _drop_zone.id == drop_zone.id);
    if (index >= 0) { this._drop_zones.splice(index, 1); }

    const subscriptions = this._drop_zone_subscriptions[drop_zone.id];
    Object.keys(subscriptions).forEach((key) => subscriptions[key].unsubscribe());
    delete this._drop_zone_subscriptions[drop_zone.id];

    drop_zone.destroy();
  }

  public addEventListener(element: HTMLElement | Document, event_name: string, cb: (event: DragEvent) => void, outside_angular: boolean = false) {
    if (outside_angular) {
      this._ngZone.runOutsideAngular(() => element.addEventListener(event_name, cb));
    } else {
      element.addEventListener(event_name, cb);
    }
  }

  public removeEventListener(element: HTMLElement | Document, event_name: string, cb: (event: DragEvent) => void) {
    element.removeEventListener(event_name, cb);
  }

  public isDropZoneRestricted(drop_zone: DropZone): boolean {
    if (this.active_draggable) {
      if (drop_zone.element.contains(this.active_draggable.element)) { return true; }

      if (drop_zone.restriction) {
        const restriction = _.isArray(drop_zone.restriction) ? (<string[]>drop_zone.restriction) : [(<string>drop_zone.restriction)];
        if (restriction.indexOf(this.active_draggable.name) >= 0) { return true; }
      }

      return false;
    }

    return true;
  }

  public getEvent(event?: DragEvent): DragAndDropEvent {
    return {
      action: this.active_draggable ? this.active_draggable.action : 'move',
      data: this.active_draggable ? this.active_draggable.data : null,
      event: event || null
    };
  }

  private _checkLeave = (event: DragEvent): void => {
    const drop_zone = this.active_drop_zone;

    if (!drop_zone) { return; }
    if (!drop_zone.dragging_over) { return; }

    if (drop_zone.element !== event.target && !drop_zone.element.contains(<Node>event.target)) {
      drop_zone.$onLeave.emit();
    }
  }
}
